import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { CloseButton, Column, Row, TextField, TextFieldWithDropdown, Typography, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { BillingStageScheduleType, StagesValueType } from '~/models';
import { utils } from '~/utils';
import { defaultScheduleForType } from './scheduleForType';
import { StageScheduleControl } from './StageScheduleControl';
const valueTypeTextMap = {
    [StagesValueType.Percent]: '%',
    [StagesValueType.Amount]: '$'
};
export const GetAutomaticStageRowTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const VALUE_WIDTH = 140;
const computeDollarAmount = (stage, valueType, totalRate)=>valueType === StagesValueType.Percent ? totalRate.mul(stage.value).div(100) : stage.value;
export const AutomaticStageRow = (param)=>{
    let { testId, stage, stageNumber, context, isFirst, isLast, canRemove, valueType, totalRate, helperText, disabled, onChange, onValueTypeChange, onRemove } = param;
    var _stage_schedule;
    const { AutomaticStageRowTestIds } = useMemo(()=>GetAutomaticStageRowTID(testId), [
        testId
    ]);
    const { t: stagesTexts } = useLocaleKeys('billing.stages');
    const { isMobileDevice } = useDevice();
    const label = stagesTexts.stageLabel({
        stageNumber
    });
    const valueDisabled = disabled || isLast;
    const dollarAmount = computeDollarAmount(stage, valueType, totalRate);
    const hasError = !!helperText;
    const valueField = isFirst ? /*#__PURE__*/ _jsx(TextFieldWithDropdown, {
        bi: {
            name: 'stage_value'
        },
        alwaysShrinkLabel: true,
        label: label,
        testId: AutomaticStageRowTestIds.Value(),
        disabled: valueDisabled,
        width: VALUE_WIDTH,
        minWidth: VALUE_WIDTH,
        error: hasError,
        helperText: helperText,
        value: {
            text: stage.value.toString(),
            option: valueType
        },
        options: Object.entries(valueTypeTextMap).map((param)=>{
            let [value, text] = param;
            return {
                value: value,
                label: text
            };
        }),
        deriveProps: (option)=>({
                isMoney: option === StagesValueType.Amount,
                isPercentage: option === StagesValueType.Percent
            }),
        onChange: (next)=>{
            const optionChanged = next.option !== valueType;
            const textChanged = next.text !== stage.value.toString();
            if (!optionChanged && !textChanged) return;
            if (textChanged) {
                const parsed = new Decimal(next.text || 0).toDecimalPlaces(2);
                onChange(_object_spread_props(_object_spread({}, stage), {
                    value: parsed
                }));
            }
            if (optionChanged) {
                onValueTypeChange(next.option);
            }
        }
    }) : /*#__PURE__*/ _jsx(TextField, {
        bi: {
            name: 'stage_value'
        },
        alwaysShrinkLabel: true,
        label: label,
        testId: AutomaticStageRowTestIds.Value(),
        disabled: valueDisabled,
        width: VALUE_WIDTH,
        minWidth: VALUE_WIDTH,
        error: hasError,
        helperText: helperText,
        isMoney: valueType === StagesValueType.Amount,
        isPercentage: valueType === StagesValueType.Percent,
        value: stage.value.toString(),
        onChange: (event)=>{
            const parsed = new Decimal(event.target.value || 0).toDecimalPlaces(2);
            onChange(_object_spread_props(_object_spread({}, stage), {
                value: parsed
            }));
        },
        suffix: /*#__PURE__*/ _jsx(Typography, {
            children: valueTypeTextMap[valueType]
        })
    });
    const removeButton = canRemove && !disabled && /*#__PURE__*/ _jsx(CloseButton, {
        bi: {
            name: 'remove_stage'
        },
        skin: "transparent",
        testId: AutomaticStageRowTestIds.Remove(),
        onClick: onRemove
    });
    const billedLine = /*#__PURE__*/ _jsxs(Row, {
        gap: 4,
        alignItems: "center",
        wrap: false,
        children: [
            /*#__PURE__*/ _jsxs(Typography, {
                testId: AutomaticStageRowTestIds.BilledAmount(),
                children: [
                    utils.formatDollarAmount(dollarAmount),
                    " ",
                    stagesTexts.schedule.inline.billed()
                ]
            }),
            /*#__PURE__*/ _jsx(StageScheduleControl, {
                testId: AutomaticStageRowTestIds.Schedule(),
                schedule: (_stage_schedule = stage.schedule) !== null && _stage_schedule !== void 0 ? _stage_schedule : defaultScheduleForType(BillingStageScheduleType.Manual),
                context: context,
                disabled: disabled,
                onChange: (schedule)=>onChange(_object_spread_props(_object_spread({}, stage), {
                        schedule
                    }))
            })
        ]
    });
    if (isMobileDevice) {
        return /*#__PURE__*/ _jsxs(Column, {
            gap: 8,
            testId: AutomaticStageRowTestIds.Root(),
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    children: [
                        valueField,
                        removeButton
                    ]
                }),
                billedLine
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        testId: AutomaticStageRowTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                children: [
                    valueField,
                    billedLine
                ]
            }),
            removeButton
        ]
    });
};
